"use client"

import { changeLanguage } from "@/actions/checkout/client/change-language"
import { changeShippingDestination } from "@/actions/checkout/client/change-shipping-destination"
import { AddressFormEnum } from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps/CustomerInformation/CustomerFormFieldGroup/AddressForm"
import Select from "@/app/(site)/_components/Atoms/Selects/select"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { getAllCountriesAndPaths } from "@/actions/selection/get-all-countries-and-paths"
import { t } from "@/translations"
import { useRouter } from "next/navigation"
import toast from "react-hot-toast"

interface SelectCountryProps {
    setIsLoadingChangeCountry?: (loading: boolean) => void
    setSelectedCountryCode?: (code: string) => void
    value?: string
    disabled?: boolean
    isLoading?: boolean
    label?: string
    name: string
    required?: boolean
    selectedCountryCode?: string
    langCode: string
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
    labelClassName?: string
    inputClassName?: string
    containerClassName?: string
    prefix?: string
    placeholder?: string
    type?: string
    autoComplete?: string
}

interface SelectOption {
    value: string
    label: string
}

/**
 * Shipping destinations a customer may pick, built once at module load.
 *
 * Sourced from `getAllCountriesAndPaths` rather than `NEXT_APP_CONFIG` so a country whose
 * only locale is still preview is not offered as a destination before its site is live.
 */
const SELECTABLE_COUNTRIES = getAllCountriesAndPaths()

const COUNTRY_OPTIONS: SelectOption[] = SELECTABLE_COUNTRIES.map(entry => ({
    value: entry.countryCode,
    label: entry.label
}))

export default function SelectCountry({
    setIsLoadingChangeCountry = () => {},
    setSelectedCountryCode = () => {},
    value,
    disabled = false,
    isLoading = false,
    label,
    name,
    required = false,
    selectedCountryCode = "",
    langCode,
    onChange,
    labelClassName,
    inputClassName,
    containerClassName,
    prefix = "",
    type,
    autoComplete = "on",
    placeholder = "",
    ...props
}: SelectCountryProps) {
    const router = useRouter()
    const { selection, location, mutate } = useSWRBasketSessionState()
    const currentLanguage = selection?.language || "en"
    const currentCountry = location?.country || ""

    const setCountry = async (countryCode: string) => {
        try {
            setIsLoadingChangeCountry(true)

            const appConfigEntry = SELECTABLE_COUNTRIES.find(
                e => e.countryCode === countryCode
            )
            if (!appConfigEntry) {
                toast.error(
                    t(langCode, "error.locale_not_supported", { locale: countryCode })
                )
                setIsLoadingChangeCountry(false)
                return
            }

            const firstLanguage = appConfigEntry.languages[0].language
            const countryName = appConfigEntry.label

            const formData = new FormData()
            formData.append("countryCode", countryCode)
            // `languageCode` is the key changeLanguage reads; anything else fails its guard.
            formData.append("languageCode", firstLanguage)
            formData.append("countryName", countryName)

            const countryChanged = currentCountry !== countryCode

            let result
            if (countryChanged) {
                result = await changeShippingDestination(formData)
                await mutate()
            } else {
                const languageChanged = location?.language?.language !== currentLanguage
                if (languageChanged) {
                    result = await changeLanguage(formData)
                    await mutate()
                } else {
                    setIsLoadingChangeCountry(false)
                    return
                }
            }

            if (result.success) {
                setIsLoadingChangeCountry(false)

                if (countryChanged) {
                    // Use the locale the API wrote. CH has several and a local lookup can pick the wrong one.
                    const targetLocalePath = result.data.basketSessionCookie?.preferredApp

                    if (targetLocalePath) {
                        const currentPath = window.location.pathname
                        const newPath = currentPath.replace(
                            /^\/[a-z]{2}(-[a-z]{2})?/,
                            targetLocalePath
                        )
                        if (newPath !== currentPath) {
                            router.push(newPath)
                            return
                        }
                    } else {
                        toast.error(
                            t(langCode, "error.locale_not_supported", {
                                locale: countryCode
                            })
                        )
                    }
                }
                return
            }

            throw new Error(
                result.error?.message ||
                    "Couldn't set country in Country State Zip Selectors"
            )
        } catch (error) {
            console.error("Error setting country:", error)
            setIsLoadingChangeCountry(false)
            toast.error(t(langCode, "error.something_went_wrong_try_again"))
        }
    }

    return (
        <Select
            options={isLoading ? [] : COUNTRY_OPTIONS}
            value={
                type === AddressFormEnum.SHIPPING_ADDRESS
                    ? currentCountry?.toUpperCase() || value || ""
                    : value || ""
            }
            onChange={e => {
                const countryCode = e.target.value
                const isShipping = type === AddressFormEnum.SHIPPING_ADDRESS

                const isUserInitiated =
                    typeof document !== "undefined" && document.activeElement === e.target
                if (isShipping && !isUserInitiated) {
                    return
                }

                onChange?.(e)
                const isUserChange = countryCode !== selectedCountryCode
                setSelectedCountryCode(countryCode)

                if (isShipping && isUserChange && countryCode) {
                    setCountry(countryCode)
                }
            }}
            name={name}
            langCode={langCode}
            isLoading={isLoading}
            label={label}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            containerClassName={containerClassName}
            placeholder={isLoading ? "Loading..." : placeholder}
            required={required}
            prefix={prefix}
            isCountrySelector={true}
            id="country-selector"
            autoComplete={autoComplete}
            {...props}
        />
    )
}
